import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import ProctoringWidget from '@shaunstapes/proctoring-widget';

const VIOLATION_TYPES = [
  'Multiple Faces Detected',
  'Low Lighting Detected',
  'High Lighting Detected',
  'Loud Sound Detected',
  'Screen Sharing Detected',
  'Network Connection Lost',
  'Face Verification Failed',
  'No Face Detected'
];

const VIOLATION_TRIGGERS = {
  'Multiple Faces Detected':
    'Start with one face in view, then add a second (person, phone, or tablet with a face photo). Wait up to ~5 s for the next detection cycle.',
  'Low Lighting Detected':
    'Darken the room or cover the camera lens so the image is clearly dark. Wait up to ~5 s.',
  'High Lighting Detected':
    'Point the camera directly at a bright window or lamp (close to the source). Watch the debug “brightness” value — it must exceed 165. Auto-exposure may cap the reading; move closer to the light source.',
  'Loud Sound Detected':
    'Allow microphone when the browser prompts (mic must show “on” in debug). Play music or talk loudly near the mic — noise level must reach 25+. If noise stays at 0, refresh and grant mic permission; audioContext must be “running”.',
  'Screen Sharing Detected':
    'Must be browser tab/window share (Chrome “Share this tab” or “Window”) — NOT Teams/Zoom desktop apps (those cannot be detected). Watch screenCapture in debug; it should become true within ~5 s.',
  'Network Connection Lost':
    'Turn off Wi‑Fi or set DevTools → Network → Offline while monitoring. Reconnect after the violation appears.',
  'Face Verification Failed':
    'Be registered for this exam. Start with your registered face visible, then cause two consecutive verify failures (~60 s apart) — turn away, use a different face, or cover the camera for two full cycles.',
  'No Face Detected':
    'Start with your face in frame, then leave the frame or cover the camera for more than 5 seconds. Show your face again to resume other checks.'
};

function emptyCheckpointState() {
  return Object.fromEntries(VIOLATION_TYPES.map((t) => [t, { cp1: false, cp2: false }]));
}

const ViolationUatPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userId, setUserId] = useState(searchParams.get('userId') || '19316');
  const [examId, setExamId] = useState(searchParams.get('examId') || '3131');
  const [start, setStart] = useState(true);
  const [checkpoints, setCheckpoints] = useState(emptyCheckpointState);
  const [logLines, setLogLines] = useState([]);
  const origLogRef = useRef(null);

  const adminUrl = useMemo(
    () => `/admin?library_id=${encodeURIComponent(examId)}&rollout_id=8699`,
    [examId]
  );

  const appendLog = useCallback((msg) => {
    setLogLines((prev) => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev].slice(0, 50));
  }, []);

  const markCp1 = useCallback((type, detail) => {
    if (!type || !VIOLATION_TYPES.includes(type)) return;
    setCheckpoints((prev) => ({
      ...prev,
      [type]: { ...prev[type], cp1: true }
    }));
    appendLog(`CP1 ${type}${detail ? ` — ${detail}` : ''}`);
  }, [appendLog]);

  const markCp2 = useCallback((type) => {
    if (!type || !VIOLATION_TYPES.includes(type)) return;
    setCheckpoints((prev) => ({
      ...prev,
      [type]: { ...prev[type], cp2: true }
    }));
    appendLog(`CP2 WS sent ${type}`);
  }, [appendLog]);

  useEffect(() => {
    origLogRef.current = console.log;
    const handler = (...args) => {
      const s = args.map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ');
      if (s.includes('[Proctoring][Widget][violation]')) {
        VIOLATION_TYPES.forEach((type) => {
          if (s.includes(type)) markCp1(type);
        });
      }
      if (s.includes('[Proctoring][Widget][WS] SENT violation')) {
        VIOLATION_TYPES.forEach((type) => {
          if (s.includes(type)) markCp2(type);
        });
      }
      origLogRef.current.apply(console, args);
    };
    console.log = handler;
    return () => {
      console.log = origLogRef.current;
    };
  }, [markCp1, markCp2]);

  const applyIds = () => {
    setSearchParams({ userId, examId });
    setCheckpoints(emptyCheckpointState());
    setLogLines([]);
    appendLog(`IDs updated user=${userId} exam=${examId}`);
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Violation UAT — Student</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open admin in another tab with the same exam ID (<code>library_id</code>).
        Use a <strong>User ID from the admin student list</strong> (e.g. 19316) — registration only appears for learners on that exam.
      </Typography>

      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          How to get a successful violation
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <strong>Before testing:</strong> Allow webcam (and mic for sound tests). Click through system check and start monitoring.
          Keep the admin dashboard open on the same exam — it should show <strong>Connected</strong>.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <strong>Success on this page:</strong> CP1 ✓ = widget detected the violation; CP2 ✓ = WebSocket sent it.
          CP3–5 = confirm the same violation appears in the admin student list and persists after refresh.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: '22%' }}>Violation type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>How to trigger</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {VIOLATION_TYPES.map((type) => (
              <TableRow key={type}>
                <TableCell sx={{ verticalAlign: 'top' }}>{type}</TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>{VIOLATION_TRIGGERS[type]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Tip: After triggering, watch for a widget toast, a new line in the log below, and CP1/CP2 checkmarks in the tracker.
          The widget debug panel shows live brightness and noise levels vs thresholds. If violationsPaused is true (no face in frame), lighting and noise checks are disabled — keep your face visible while testing.
        </Typography>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Paper sx={{ p: 2 }}>
          <StackRow>
            <TextField label="User ID" size="small" value={userId} onChange={(e) => setUserId(e.target.value)} />
            <TextField label="Exam ID" size="small" value={examId} onChange={(e) => setExamId(e.target.value)} />
            <Button variant="outlined" onClick={applyIds}>Apply IDs</Button>
          </StackRow>
          <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
            <Link to={adminUrl} target="_blank" rel="noopener noreferrer">Open admin dashboard</Link>
          </Typography>
          <ProctoringWidget
            key={`${userId}-${examId}`}
            userId={userId}
            examId={examId}
            start={start}
            examIsActive
            isExamActive
            sendViolationsToServer
            requireSystemCheck={false}
            showDebug
            showVideo
            apiBaseUrl={origin}
            wsUrl={origin}
            modelsBaseUrl="/models"
            onViolation={(v) => {
              if (v?.type) markCp1(v.type, v.clientViolationId || '');
            }}
          />
          <Button sx={{ mt: 1 }} size="small" onClick={() => setStart((s) => !s)}>
            {start ? 'Pause monitoring' : 'Resume monitoring'}
          </Button>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>5-checkpoint tracker</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Violation type</TableCell>
                <TableCell>CP1 Widget</TableCell>
                <TableCell>CP2 WS</TableCell>
                <TableCell>CP3–5</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {VIOLATION_TYPES.map((type) => {
                const s = checkpoints[type];
                const tick = (ok) => (ok ? '✓' : '—');
                return (
                  <TableRow key={type}>
                    <TableCell>{type}</TableCell>
                    <TableCell>{tick(s.cp1)}</TableCell>
                    <TableCell>{tick(s.cp2)}</TableCell>
                    <TableCell>admin console / DB</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Box
            component="pre"
            sx={{
              mt: 2,
              p: 1,
              maxHeight: 200,
              overflow: 'auto',
              bgcolor: '#111',
              color: '#0f0',
              fontSize: 11
            }}
          >
            {logLines.length ? logLines.join('\n') : 'Waiting for violations…'}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

function StackRow({ children }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      {children}
    </Box>
  );
}

export default ViolationUatPage;
